const ids = ( value = '' ) => value.split( ',' ).filter( Boolean );
const overlaps = ( selected, actual ) => actual !== null && selected.some( id => actual.includes( id ) );

export function isDue( rule, lastShown, now = Date.now() ){
	if( rule.frequency === 'event' || rule.frequency === 'page' || lastShown === null ) return true;
	return rule.frequency === 'days' && now - Number( lastShown ) >= Math.max( 1, rule.days ) * 86400000;
}

export function matchesCartItem( rule, productId, collections ){
	const id = String( productId );
	if( !productId || rule.excludedProducts.includes( id ) ) return false;
	if( rule.excludedCollections.length && collections === null ) return false;
	return !overlaps( rule.excludedCollections, collections ) &&
		( rule.products.includes( id ) || overlaps( rule.collections, collections ) );
}

export function firstReady( entries, ready ){
	return entries.find( entry => ready.has( entry ) );
}

let automaticShown = false;
let editorBlockId = null;

if( typeof window !== 'undefined' && !customElements.get( 'khama-modals' ) ){
	customElements.define( 'khama-modals', class extends HTMLElement {
		async connectedCallback(){
			this.abort?.abort();
			this.abort = new AbortController();
			const { signal } = this.abort;
			this.entries = null;
			if( window.Shopify?.designMode ){
				document.addEventListener( 'shopify:block:select', event => this.preview( event, true ), { capture: true, signal } );
				document.addEventListener( 'shopify:block:deselect', event => this.preview( event, false ), { capture: true, signal } );
			}
			await customElements.whenDefined( 'x-modal' );
			if( signal.aborted || !this.isConnected ) return;

			this.entries = Array.from( this.querySelectorAll( 'x-modal[data-khama-modal]' ), modal => ( {
				modal,
				rule: {
					key: `khama:modal:${ this.dataset.sectionId }:${ modal.dataset.khamaModal.split( '__' ).pop() }`,
					trigger: modal.dataset.trigger,
					frequency: modal.dataset.frequency,
					days: Number( modal.dataset.days ) || 7,
					products: ids( modal.dataset.products ),
					collections: ids( modal.dataset.collections ),
					excludedProducts: ids( modal.dataset.excludedProducts ),
					excludedCollections: ids( modal.dataset.excludedCollections )
				}
			} ) );
			this.ready = new Set();
			this.transitions = new Set();
			this.collectionCache = new Map();
			this.timers = [];
			this.section = this.closest( '.shopify-section' );
			this.section?.addEventListener( 'shopify:section:unload', () => this.cleanup(), { signal } );

			if( window.Shopify?.designMode ){
				this.entries.forEach( ( { modal } ) => modal.addEventListener( 'dialog:after-hide', () => {
					if( editorBlockId ) this.preview( { detail: { blockId: editorBlockId, load: true } }, true );
				}, { signal } ) );
				if( editorBlockId ) this.preview( { detail: { blockId: editorBlockId } }, true );
				return;
			}

			for( const name of [ 'dialog:before-show', 'dialog:before-hide', 'dialog:after-show', 'dialog:after-hide' ] ){
				document.addEventListener( name, event => {
					if( name.includes( ':before-' ) ) this.transitions.add( event.target );
					else this.transitions.delete( event.target );
					this.schedule();
				}, { capture: true, signal } );
			}
			document.addEventListener( 'visibilitychange', () => this.schedule(), { signal } );
			document.addEventListener( 'variant:add', event => this.onCartAdd( event ).catch( () => {} ), { signal } );

			for( const entry of this.entries ){
				if( !this.eligible( entry ) ) continue;
				const { modal, rule } = entry;
				if( rule.trigger === 'time' ){
					this.timers.push( setTimeout( () => this.request( entry ), Math.max( 0, Number( modal.dataset.seconds ) || 0 ) * 1000 ) );
				}else if( rule.trigger === 'visit' && ( modal.dataset.visitMatch === 'true' || modal.querySelector( '[data-khama-page-match="true"]' ) ) ){
					this.request( entry );
				}
			}

			if( this.entries.some( entry => entry.rule.trigger === 'scroll' && this.eligible( entry ) ) ){
				window.addEventListener( 'scroll', () => {
					const distance = document.documentElement.scrollHeight - window.innerHeight;
					for( const entry of this.entries ){
						if( entry.rule.trigger !== 'scroll' ) continue;
						const reached = distance > 0 && window.scrollY / distance * 100 >= Number( entry.modal.dataset.scroll );
						if( reached && !entry.scrollReached ) this.request( entry );
						entry.scrollReached = reached;
					}
				}, { passive: true, signal } );
			}

			if( window.matchMedia( '(hover: hover) and (pointer: fine)' ).matches && this.entries.some( entry => entry.rule.trigger === 'exit' && this.eligible( entry ) ) ){
				let pointerInside = false;
				document.addEventListener( 'pointermove', event => { if( event.pointerType === 'mouse' ) pointerInside = true; }, { signal } );
				document.addEventListener( 'pointerout', event => {
					if( !pointerInside || event.pointerType !== 'mouse' || event.relatedTarget !== null || event.clientY > 0 ) return;
					pointerInside = false;
					for( const entry of this.entries ){
						if( entry.rule.trigger === 'exit' ) this.request( entry );
					}
				}, { signal } );
			}
		}

		disconnectedCallback(){
			this.cleanup();
		}

		cleanup(){
			this.abort?.abort();
			if( this.focusFrame ) cancelAnimationFrame( this.focusFrame );
			this.timers?.forEach( clearTimeout );
			this.entries?.forEach( ( { modal } ) => modal.remove() );
			this.ready?.clear();
			this.pending = null;
		}

		storage( rule ){
			try {
				return rule.frequency === 'session' ? window.sessionStorage : window.localStorage;
			}catch {
				return null;
			}
		}

		eligible( entry ){
			if( entry.modal.open || this.transitions.has( entry.modal ) ) return false;
			if( entry.rule.frequency === 'event' ) return true;
			if( automaticShown ) return false;
			let lastShown = null;
			try { lastShown = this.storage( entry.rule )?.getItem( entry.rule.key ) ?? null; }catch {}
			return isDue( entry.rule, lastShown );
		}

		request( entry ){
			if( this.abort.signal.aborted || !this.eligible( entry ) ) return;
			this.ready.add( entry );
			this.schedule();
		}

		schedule(){
			if( this.scheduled || this.abort.signal.aborted ) return;
			this.scheduled = true;
			queueMicrotask( () => {
				this.scheduled = false;
				this.flush();
			} );
		}

		flush(){
			if( this.abort.signal.aborted ) return;
			if( !this.pending ) this.pending = firstReady( this.entries.filter( entry => this.eligible( entry ) ), this.ready );
			if( !this.pending || document.hidden ) return;
			const { modal, rule } = this.pending;
			const cart = document.querySelector( 'cart-drawer' );
			const dialogs = new Set( [ ...document.querySelectorAll( '[role="dialog"][open], dialog[open]' ), ...this.transitions ] );
			if( Array.from( dialogs ).some( dialog => dialog !== modal && !( rule.trigger === 'cart' && dialog === cart && cart.open && !this.transitions.has( cart ) ) ) ) return;

			automaticShown = true;
			this.pending = null;
			this.ready.clear();
			if( this.focusFrame ) cancelAnimationFrame( this.focusFrame );
			modal.addEventListener( 'dialog:after-show', () => {
				if( rule.frequency === 'page' || rule.frequency === 'event' ) return;
				try { this.storage( rule )?.setItem( rule.key, String( Date.now() ) ); }catch {}
			}, { once: true, signal: this.abort.signal } );
			if( rule.trigger === 'cart' && cart?.open ){
				let returnFocus = document.activeElement;
				while( returnFocus?.shadowRoot?.activeElement ) returnFocus = returnFocus.shadowRoot.activeElement;
				if( !cart.contains( returnFocus ) && !cart.shadowRoot?.contains( returnFocus ) ) returnFocus = cart.getShadowPartByName?.( 'close-button' ) || cart;
				returnFocus.focus( { preventScroll: true } );
				modal.addEventListener( 'dialog:after-hide', () => {
					this.focusFrame = requestAnimationFrame( () => {
						if( !this.abort.signal.aborted && cart.isConnected && cart.open ) returnFocus.focus( { preventScroll: true } );
					} );
				}, { once: true, signal: this.abort.signal } );
			}
			modal.show();
		}

		async collectionsFor( item ){
			const key = String( item.product_id );
			if( key === this.dataset.productId ) return ids( this.dataset.collectionIds );
			if( !this.collectionCache.has( key ) ){
				this.collectionCache.set( key, ( async () => {
					try {
						const url = new URL( item.url, window.location.origin );
						if( url.origin !== window.location.origin || !url.pathname.includes( '/products/' ) ) return null;
						url.searchParams.set( 'section_id', this.dataset.sectionId );
						const response = await fetch( url, { signal: this.abort.signal } );
						if( !response.ok ) return null;
						const section = new DOMParser().parseFromString( await response.text(), 'text/html' ).querySelector( 'khama-modals' );
						return section?.dataset.productId === key ? ids( section.dataset.collectionIds ) : null;
					}catch {
						return null;
					}
				} )() );
			}
			return this.collectionCache.get( key );
		}

		async onCartAdd( event ){
			const entries = this.entries.filter( entry => entry.rule.trigger === 'cart' && this.eligible( entry ) );
			if( !entries.length || !Array.isArray( event.detail?.items ) ) return;
			const needsCollections = entries.some( ( { rule } ) => rule.collections.length || rule.excludedCollections.length );
			const items = await Promise.all( event.detail.items.filter( item => item?.product_id ).map( async item => ( {
				id: item.product_id,
				collections: needsCollections ? await this.collectionsFor( item ) : null
			} ) ) );
			for( const entry of entries ){
				if( items.some( item => matchesCartItem( entry.rule, item.id, item.collections ) ) ) this.request( entry );
			}
		}

		preview( event, show ){
			if( show ) editorBlockId = event.detail.blockId;
			else if( editorBlockId === event.detail.blockId ) editorBlockId = null;
			const entry = this.entries?.find( ( { modal } ) => [ modal, ...modal.querySelectorAll( '[data-khama-page-block]' ) ].some( element => {
				const editorId = JSON.parse( element.dataset.shopifyEditorBlock || '{}' ).id;
				return [ editorId, element.dataset.khamaModal, element.dataset.khamaPageBlock ].includes( event.detail.blockId );
			} ) );
			if( !entry ) return;
			if( show ){
				this.entries.forEach( ( { modal } ) => { if( modal !== entry.modal && modal.open ) modal.hide(); } );
				if( !entry.modal.open ) entry.modal.show( { animate: !event.detail.load } );
			}else if( entry.modal.open ){
				entry.modal.hide();
			}
		}
	} );
}
